// Реализация фаз хода матча.
// Фаза 3 (бой) сделана async — UI оркестрирует анимацию через animator.
// Для smoke-test и headless: NULL_ANIM — все методы no-op.

import { unitVsUnit, unitVsHero, heroVsUnit, heroVsHero, canAttackHero, applyDamage } from './combat.js';
import { selectAttackTarget } from './priorities.js';
import { hasKeyword, removeKeyword, KW, warnIfUnknownKeywords } from './keywords.js';
import { shortHeroName } from '../data/heroes.js';
import {
  onHeroAttack, onUnitDamaged, onUnitDied, onHeroDamaged, onEndOfTurn, onTurnStart,
  runUnitOrdersOnReveal, runArmedSpyAttacks
} from './abilities/deus/index.js';
import { redirectToSekretar, applyHeroDamage } from './hero-damage.js';
import { resolveAction as deusResolveAction } from './abilities/deus/actions.js';
import { resolveAction as synResolveAction } from './abilities/syntetics/actions.js';
import { resolveAction as glbResolveAction } from './abilities/global/actions.js';
import { resolveAction as livResolveAction } from './abilities/livlife/actions.js';
// C.1 Этап 45: нейтральные Действия (Прайс-лист).
import { resolveAction as neutralResolveAction } from './abilities/neutral/actions.js';
// В.1.c-1: Расчёт тяжёлого пулемёта — контратака на атаку taunt-союзника.
import { raschetOnAllyAttacked } from './abilities/global/raschet.js';
// В.1.c-2: Офицер связи (line 2) использует историю разыгранных Действий.
import { rememberLastAction } from './abilities/dispatch.js';
import { isInfiltratorBackline } from './abilities/deus/infiltrator.js';
import { CARD_ID as SPY_ID } from './abilities/deus/spyaschiy-agent.js';
import { applyOverloadDeaths, revealPendingMods } from './abilities/syntetics/modifications.js';
import { applyPendingResurrects } from './abilities/syntetics/vtoroe-serdtse.js';
import { tickKustarnyyDoT } from './abilities/syntetics/kustarnyy-stimulyator.js';
// АНИМ.14-fix: DoT от Заражения (Лив Лайф) — раньше не подключался,
// keyword бесполезно стакался без урона.
import { tickInfectedDoT } from './keywords/infected.js';
// В.1.f-1: transient-keyword'ы со сроком жизни (Перегруппировка → frenzy на ход).
import { cleanupTransientKeywords } from './keywords/transient.js';
// В.1.f-2-A: временные баффы (Пропаганда → +1/1/1 на 2 хода).
import { expireTemporaryBuffs } from './buffs.js';
// В.2.a: Лив Лайф — двухфазный хил-проход в Ф3 (раунды 1 и 2).
import { runLivLifeHealAuras } from './abilities/livlife/heal-auras.js';
// В.2.d-1: keyword immune — истечение источников в начале Ф1.
import { expireImmuneSources } from './keywords/immune.js';
// Этап 27: TTL Молчания от Действий (Глушилка GS-9, Тотальная глушилка).
import { tickSilenceTTL } from './keywords/silence.js';
// Этап 21: единое правило инициации по редкости.
import { initiationForRarity } from './initiation.js';

// Б.2.b / В.1.a-fix3 / В.1.f-1: faction-aware резолв action-карт.
// 5-й аргумент opts — для Действий с расширенным выбором цели (Перегруппировка
// Глобала: line+slot). Существующие резолверы 5-й аргумент игнорируют.
function resolveAction(state, playerIdx, cardId, target, opts = {}) {
  // C.1 Этап 33: v6 ID имеют префиксы `global_*` и `livlife_*`,
  // старые — `glb_*` и `liv_*`. Раньше v6-Действия Глобала/ЛивЛайфа
  // уходили в default deus → unknown_action → эффект не применялся.
  const sId = typeof cardId === 'string' ? cardId : '';
  if (sId.startsWith('syn_')) {
    return synResolveAction(state, playerIdx, cardId, target, opts);
  }
  if (sId.startsWith('glb_') || sId.startsWith('global_')) {
    return glbResolveAction(state, playerIdx, cardId, target, opts);
  }
  if (sId.startsWith('liv_') || sId.startsWith('livlife_')) {
    return livResolveAction(state, playerIdx, cardId, target, opts);
  }
  if (sId.startsWith('neutral_')) {
    return neutralResolveAction(state, playerIdx, cardId, target, opts);
  }
  return deusResolveAction(state, playerIdx, cardId, target, opts);
}

// Минимальный ход розыгрыша Действия по редкости. Этап 21: единое правило
// с юнитами (common=1, rare=3, epic=4, legendary=5). См. initiation.js.
// Действие, разыгранное раньше — сгорает без эффекта (см. resolvePendingActions).
export function actionInitiationTurn(rarity) {
  return initiationForRarity(rarity);
}

// Заглушка-аниматор: ничего не делает, всё мгновенно.
export const NULL_ANIM = {
  async wait() {},
  async revealCard() {},
  async beforeAttack() {},
  async afterAttack() {},
  async death() {},
  async heroDeath() {},
  async pause() {},
  // АНИМ.6: spec-эффекты. Optional, движок проверяет typeof перед вызовом.
  async heroImmune() {},
  async stealthActivate() {},
  // АНИМ.7: Действия + перенаправление.
  async playAction() {},
  async heal() {},
  async buff() {},
  async summon() {},
  async redirect() {},
  async damage() {},
  // АНИМ.8: Парашют.
  async parachute() {},
  // АНИМ.9: Расчёт тяжёлого пулемёта — прерывающий выстрел.
  async raschet() {},
  // АНИМ.11: перемещение юнита между ячейками.
  async move() {},
  // АНИМ.11: Деус разыгрывает карту Влияния.
  async influence() {},
  // АНИМ.14: stack-up индикатор для Mod / Infected.
  async stackUp() {},
  // АНИМ.15: keyword attach/remove flash.
  async keywordFlash() {},
  // АНИМ.15: Phase transition banner.
  async phaseBanner() {},
  // АНИМ.16: card draw / placement movement.
  async cardDraw() {},
  async cardPlacement() {},
  rerender() {},
  shouldStop() { return false; }
};

// Сколько урона реально понесла цель (броня + ХП).
function dmgTaken(r) {
  return (r?.armorAbsorbed || 0) + (r?.hpLost || 0);
}

// А.5: список юнитов одной стороны, способных атаковать в Ф3.
// Порядок: line 0 слева→направо, потом line 1 слева→направо.
// Стелс / atk<=0 / cantAttack / Спящий агент — отфильтрованы.
// Б.2.a: юниты с Неистовством (FRENZY) добавляются ДВАЖДЫ — атакуют две атаки
// за Ф3. Существующие правила ("контратаковал — пасует", "свежевыставленный по
// Герою не бьёт", стелс) применяются к каждой автоатаке отдельно.
function collectPhase3Attackers(player) {
  const out = [];
  for (let line = 0; line < 2; line++) {
    for (let slot = 0; slot < 4; slot++) {
      const u = player.field[line][slot];
      if (!u) continue;
      if (hasKeyword(u, KW.STEALTH)) continue;
      if (u.atk <= 0) continue;
      if (u.cantAttack) continue;
      if (u.id === SPY_ID) continue;
      out.push(u);
      if (hasKeyword(u, KW.FRENZY)) out.push(u);
    }
  }
  return out;
}

// === Фаза 1: Тяга ===
//
// В.1.f-2-B: в замороженных ходах (Рой дронов) выполняется ТОЛЬКО добор —
// без снятия временных баффов, Перегрузки, DoT, воскрешений и триггеров
// начала хода. Если Б.2.c-триггер на урон по Герою (от пустой колоды)
// нужен — он остаётся, потому что урон от тяги — это часть самой Ф1.
export function phaseDraw(state) {
  state.log.add(`— Фаза 1: тяга —`);
  const frozen = state.swarmFrozenUntilTurn != null
    && state.currentTurn <= state.swarmFrozenUntilTurn;
  if (!frozen) {
    // В.1.f-2-A: снять истёкшие временные баффы ДО любых триггеров и
    // автовосстановления брони. Юнит может умереть здесь, если был ранен —
    // это норма по балансу.
    expireTemporaryBuffs(state);
    // В.2.d-1: снять истёкшие источники Иммунитета.
    expireImmuneSources(state);
    // Этап 27: TTL Молчания от Действий (Глушилка, Тотальная глушилка).
    tickSilenceTTL(state);
    // Б.1.b: Перегрузка — гибель юнитов с modifications.length >= overloadThreshold.
    // ДО тяги, чтобы Посмертия и Сина-trigger корректно отработали в начале хода.
    applyOverloadDeaths(state);
    // Б.2.b: DoT Кустарного стимулятора. Тикает 1 урон в Ф1 КАЖДОГО раунда после
    // раунда розыгрыша (placedTurn < currentTurn). Через applyDamage — броня
    // поглощает, триггеры на полученный урон работают.
    tickKustarnyyDoT(state);
    // АНИМ.14-fix: DoT от Заражения (Лив Лайф) для обоих игроков —
    // ход в этой игре общий, поэтому тикают все заражённые юниты на поле.
    for (let idx = 0; idx < 2; idx++) {
      const events = tickInfectedDoT(state, idx);
      // Триггеры на damage / death для убитых от Заражения юнитов.
      for (const ev of events) {
        const u = ev.unit;
        const taken = (ev.result?.armorAbsorbed || 0) + (ev.result?.hpLost || 0);
        if (taken > 0) {
          onUnitDamaged(state, { ownerIdx: idx, card: u, dmg: taken });
        }
        if (u && !u.alive) {
          onUnitDied(state, {
            ownerIdx: idx, card: u,
            killer: null, killerIdx: null, killerWasCounter: false
          });
        }
      }
    }
  }
  for (const p of state.players) {
    const card = p.drawOne();
    if (card) {
      if (p.hand.length >= 10) {
        p.discard.push(card);
        state.log.add(`${p.name}: рука переполнена, "${card.name_ru}" сгорает`);
      } else {
        p.hand.push(card);
        state.log.add(`${p.name} тянет карту (в руке: ${p.hand.length})`);
      }
    } else {
      p.hero.hp -= 1;
      state.log.add(`${p.name}: колода пуста, ${shortHeroName(p.hero)} получает 1 урона (ХП: ${p.hero.hp})`);
      // Б.2.c: триггер урона по Герою (Тёмный нетраннер).
      // В замороженных ходах — пропускаем (никаких триггеров вообще).
      if (!frozen) onHeroDamaged(state, p.id, 1);
    }
  }
  if (frozen) return;
  // Б.2.a: воскрешения через Второе сердце — после тяги обоих игроков.
  // Лог чище: "тяга → возрождение → триггеры начала хода".
  applyPendingResurrects(state);
  // А.4.4.3: триггеры «в начале каждого хода» (ИИ-ядро 2-й линии — карта Влияния в руку).
  onTurnStart(state);
}

// АНИМ.10: async-обёртка над phaseDraw с diff-snapshot — нужна чтобы
// tickKustarnyyDoT (damage от DoT) и applyPendingResurrects (summon
// воскресшего юнита) проигрывались визуально. Не дублирует phaseDraw,
// просто оборачивает в _withDiffAnim. Используется из match.js
// startTurnAnimated (через PvP/PvE с anim). Sync phaseDraw остаётся для
// тестов и headless-сценариев.
//
// АНИМ.16: дополнительно играем cardDraw анимацию для каждой тяги —
// ghost карты летит из колоды в руку.
export async function phaseDrawAnimated(state, anim = NULL_ANIM) {
  // Не frozen (Рой дронов) — тяга будет, анимируем.
  const frozen = state.swarmFrozenUntilTurn != null
    && state.currentTurn <= state.swarmFrozenUntilTurn;
  // Anim card draw ДО sync phaseDraw — пока колоды ещё полные.
  if (!frozen || frozen) {  // в frozen ходу тяга всё равно идёт (только Ф1)
    for (let i = 0; i < 2; i++) {
      if (state.players[i].deck.length > 0
          && typeof anim.cardDraw === 'function') {
        const faction = state.players[i].hero?.faction || 'neutral';
        await anim.cardDraw(i, faction);
      }
    }
  }
  await _withDiffAnim(state, anim, (s) => phaseDraw(s));
}

// === Фаза 3: Спящий агент → Вскрытие → Действия → Order → авто-атаки (async) ===
export async function runPhase3(state, anim = NULL_ANIM) {
  state.log.add(`— Фаза 3: бой —`);
  // АНИМ.15: phase banner — переход в Ф3 (Бой).
  if (typeof anim.phaseBanner === 'function') await anim.phaseBanner(3, 'Бой');

  // 0) А.4.4.4-fix1: армированные Спящие срабатывают ПЕРВЫМИ.
  await runArmedSpyAttacks(state, anim);
  if (state.players[0].hero.hp <= 0 || state.players[1].hero.hp <= 0) return;

  // 0.5) Б.1.c: раскрытие Модов Синтетика — урон Герою + attachMod.
  // ДО вскрытия юнитов: бонусы от Мода (атака/броня/keywords) к моменту авто-атак уже применены.
  // АНИМ.10: diff-snapshot чтобы анимировать damage по Герою + buff'ы юнитов от Мода.
  await _withDiffAnim(state, anim, (s) => revealPendingMods(s));
  if (state.players[0].hero.hp <= 0 || state.players[1].hero.hp <= 0) return;

  // 1) Вскрытие
  for (const p of state.players) {
    for (const u of p.unitsOnField()) {
      if (!u.revealed) {
        u.revealed = true;
        if (u.placedTurn === null) u.placedTurn = state.currentTurn;
        anim.rerender();
        await anim.revealCard(u);
        const lineName = u.line === 0 ? '1-ю' : '2-ю';
        const sleep = u.isSleeping(state.currentTurn) ? ' (Режим ожидания)' : '';
        state.log.add(`↓ ${u.name_ru} [${p.name}] вскрыта на ${lineName} линию${sleep}`);
        warnIfUnknownKeywords(u);
      }
    }
  }

  // 1.5) Действия (резолв в порядке: первый игрок, потом второй).
  await resolvePendingActions(state, anim);
  if (state.players[0].hero.hp <= 0 || state.players[1].hero.hp <= 0) return;

  // 1.6) Order'ы юнитов, размещённых в этом ходу (Кризис-менеджер +0/2/0 Герою).
  // АНИМ.10: diff-snapshot чтобы heal/buff/damage от Order'ов анимировались.
  await _withDiffAnim(state, anim, (s) => runUnitOrdersOnReveal(s));

  // 2) Активация ключевых слов: первый раз, когда юнит активен (вскрыт + не спит).
  for (const p of state.players) {
    for (const u of p.unitsOnField()) {
      if (u.activated) continue;
      if (!u.isAlive() || !u.revealed || u.isSleeping(state.currentTurn)) continue;
      u.activated = true;
      if (hasKeyword(u, KW.TAUNT)) {
        state.log.add(`🛡 ${u.name_ru} [${p.name}]: Провокация`);
      }
      if (hasKeyword(u, KW.STEALTH)) {
        state.log.add(`👁 ${u.name_ru} [${p.name}]: Скрытность (ждёт ручной активации)`);
      }
    }
  }

  // А.5: сброс флага «контратаковал в этой Ф3» — для всех юнитов на поле.
  for (const p of state.players) {
    for (let line = 0; line < 2; line++) {
      for (let slot = 0; slot < 4; slot++) {
        const u = p.field[line][slot];
        if (u) u.counterAttackedThisPhase = false;
      }
    }
  }

  // В.2.a: Лив Лайф — раунд 1 хил-Аур (ДО авто-атак, диздок 6.4).
  // Без зарегистрированных Аур — no-op.
  runLivLifeHealAuras(state, 1);

  // 3) БУГ.10: SEQUENTIAL очередь авто-атак (по диздоку §6.4).
  // Сначала ВСЕ юниты первого игрока (1-я линия слева направо, потом 2-я),
  // потом ВСЕ юниты второго игрока (1-я линия, потом 2-я).
  // Раньше был zigzag A[0],B[0],A[1],B[1] — атаки воспринимались как
  // параллельные/смешанные. collectPhase3Attackers уже собирает в порядке
  // line0[0..3] → line1[0..3].
  // А.4.1-fix4: стелс-юниты не входят в очередь (атакуют вручную в Ф4).
  // А.4.4.2-fix1: current.atk <= 0 → юнит не атакует.
  // А.4.4.4: Спящий агент имеет свою логику (runArmedSpyAttacks).
  const sideAIdx = state.firstPlayerIdx;
  const sideBIdx = 1 - state.firstPlayerIdx;
  const sideA = collectPhase3Attackers(state.players[sideAIdx]);
  const sideB = collectPhase3Attackers(state.players[sideBIdx]);
  const order = [
    ...sideA.map((unit) => ({ unit, ownerIdx: sideAIdx })),
    ...sideB.map((unit) => ({ unit, ownerIdx: sideBIdx }))
  ];

  for (const { unit, ownerIdx } of order) {
    // Если кто-то из Героев погиб в ходе фазы — прекращаем.
    if (state.players[0].hero.hp <= 0 || state.players[1].hero.hp <= 0) break;
    if (!unit.isAlive()) continue;
    if (unit.isSleeping(state.currentTurn)) continue;
    // Defensive: если юнит каким-то образом получил стелс уже после сборки очереди — пропускаем.
    if (hasKeyword(unit, KW.STEALTH)) continue;

    const owner = state.players[ownerIdx];
    // А.5: «контратаковал — пасует». Юнит, отдавший контратаку в этой Ф3,
    // пропускает свою автоатаку (на следующие атаки противника он всё ещё контратакует).
    if (unit.counterAttackedThisPhase) {
      state.log.add(`↩ ${unit.name_ru} [${owner.name}]: автоатака пропущена (уже контратаковал)`);
      await anim.pause();
      continue;
    }

    const enemy = state.players[1 - ownerIdx];

    const sel = selectAttackTarget(unit, enemy, state.rng);

    for (const sk of sel.stealthSkipped) {
      state.log.add(`👁 ${sk.name_ru} [${enemy.name}] невидим — пропуск`);
    }

    const justPlaced = unit.placedTurn === state.currentTurn;
    if (justPlaced && sel.kind === 'hero') {
      state.log.add(`${unit.name_ru} [${owner.name}]: первый ход — атака пропущена`);
      await anim.pause();
      continue;
    }

    if (sel.kind === 'skip') {
      state.log.add(`${unit.name_ru} [${owner.name}]: нет доступной цели — атака пропущена`);
      await anim.pause();
      continue;
    }

    let target = sel.target;
    // АНИМ.7-fix-redirect: для Taunt-redirect используем sel.originalTarget
    // (выставлен в selectAttackTarget). Для других случаев — sel.target.
    const origTarget = sel.originalTarget || sel.target;
    let redirectedToSekretar = false;
    let infiltratorBlockedHero = false;

    // А.4.4.3: Инфильтратор на 2-й линии не атакует Героя.
    if (sel.kind === 'hero' && isInfiltratorBackline(unit)) {
      infiltratorBlockedHero = true;
      state.log.add(`${unit.name_ru} [${owner.name}]: Инфильтратор на 2-й линии — Героя не атакует, пропуск`);
      await anim.pause();
      continue;
    }
    // А.4.4.3: Секретарь — перенаправление урона по Герою на себя (passive).
    if (sel.kind === 'hero') {
      const r = redirectToSekretar(enemy, target);
      if (r.redirectedToSekretar) {
        target = r.target;
        redirectedToSekretar = true;
      }
    }
    if (sel.redirectedToTaunt) {
      state.log.add(`↪ Перенаправлено на ${target.name_ru} (Провокация)`);
      // АНИМ.7: жёлтая стрелка от исходной цели к taunt'у.
      if (typeof anim.redirect === 'function' && origTarget && origTarget !== target) {
        await anim.redirect(origTarget, target, 'taunt');
      }
    }
    if (redirectedToSekretar) {
      state.log.add(`↪ Перенаправлено на Секретаря [${enemy.name}]`);
      // АНИМ.7: жёлтая стрелка от Героя на Секретаря.
      if (typeof anim.redirect === 'function' && origTarget && origTarget !== target) {
        await anim.redirect(origTarget, target, 'sekretar');
      }
    }

    // Перевычислим kind после возможного перенаправления на Секретаря (теперь это юнит).
    const effectiveKind = (target.type === 'hero') ? 'hero' : 'unit';
    sel.kind = effectiveKind;

    if (sel.kind === 'hero') {
      // Иммунитет — урон 0, лог.
      if (enemy.heroImmuneUntilTurn != null && state.currentTurn <= enemy.heroImmuneUntilTurn) {
        state.log.add(`🛡 ${shortHeroName(target)} [${enemy.name}]: Иммунитет — атака ${unit.name_ru} блокирована`);
        // АНИМ.6: золотой щит на Hero.
        if (typeof anim.heroImmune === 'function') await anim.heroImmune(target);
        await anim.pause();
        continue;
      }
      const beforeArmor = target.armor;
      await anim.beforeAttack(unit, target);
      const r = unitVsHero(unit, target, { state });
      // Парашют — откат смертельного удара.
      let parachuteFired = false;
      if (target.hp <= 0 && enemy.parachuteActive) {
        target.hp = 1;
        target.armor = beforeArmor;
        enemy.parachuteActive = false;
        enemy.heroImmuneUntilTurn = state.currentTurn;
        parachuteFired = true;
      }
      anim.rerender();
      state.log.add(formatAttackLog(unit, owner, target, enemy, r, /*isHeroTarget=*/ true));
      if (parachuteFired) {
        state.log.add(`🪂 Золотой парашют [${enemy.name}]: смертельный удар отменён, ${shortHeroName(target)} HP=1, Иммунитет до конца хода`);
      }
      // Б.2.c: триггер «союзный персонаж получил урон» (Тёмный нетраннер).
      if (!parachuteFired) {
        const heroTaken = dmgTaken(r.fwd);
        if (heroTaken > 0) onHeroDamaged(state, 1 - ownerIdx, heroTaken);
      }
      await anim.afterAttack(unit, target, r.atkDmg);
      // АНИМ.8: парашют ПОСЛЕ afterAttack — юзер видит удар, затем
      // золотой парашют спасает.
      if (parachuteFired && typeof anim.parachute === 'function') {
        await anim.parachute(target);
      }

      if (target.hp <= 0) {
        await anim.heroDeath(target);
        state.log.add(`💀 ${shortHeroName(target)} [${enemy.name}] пал`);
      }
    } else {
      await anim.beforeAttack(unit, target);
      const noCounter = isInfiltratorBackline(unit);
      // В.1.c-1: Расчёт тяжёлого пулемёта — стреляет ДО разрешения боя, если
      // цель — союзный для Расчёта juniт с Провокацией. Cрабатывает на момент
      // начала атаки независимо от выживания союзника-taunt.
      const raschetShots = raschetOnAllyAttacked(state, ownerIdx, unit, 1 - ownerIdx, target);
      for (const shot of raschetShots) {
        // АНИМ.9: пунктирный красный луч от Расчёта к атакующему-unit'у.
        if (typeof anim.raschet === 'function' && shot.raschet) {
          await anim.raschet(shot.raschet, unit);
        }
        const taken = (shot.dmg.armorAbsorbed || 0) + (shot.dmg.hpLost || 0);
        if (taken > 0) {
          onUnitDamaged(state, { ownerIdx, card: unit, dmg: taken });
        }
        if (unit.hp <= 0) {
          owner.removeFromField(unit);
          state.log.add(`✗ ${unit.name_ru} [${owner.name}] погибает (от Расчёта)`);
          onUnitDied(state, {
            ownerIdx, card: unit,
            killer: shot.raschet, killerIdx: 1 - ownerIdx, killerWasCounter: false
          });
          break;
        }
      }
      // Если атакующий умер от выстрела Расчёта — пропускаем остальную часть боя.
      if (!unit.isAlive()) {
        // ПвП.8-fix-stuck-ghost: anim.beforeAttack уже запушил attack_start,
        // а afterAttack НЕ будет вызван → orphan event → ghost зависает.
        if (typeof anim.rewindAttack === 'function') anim.rewindAttack();
        await anim.pause();
        continue;
      }
      const r = unitVsUnit(unit, target, state.currentTurn, { noCounter, state });
      anim.rerender();
      state.log.add(formatAttackLog(unit, owner, target, enemy, r, /*isHeroTarget=*/ false));
      if (r.defDmg > 0) {
        state.log.add(formatCounterLog(target, enemy, unit, owner, r));
        // А.5: цель отдала контратаку — она теряет право на свою автоатаку в этой Ф3.
        // Срабатывает даже посмертно (флаг переживёт removeFromField — целевой объект остаётся).
        target.counterAttackedThisPhase = true;
      } else if (r.counterSuppressed && target.atk > 0) {
        state.log.add(`👤 Инфильтратор на 2-й линии — без ответного удара`);
      } else if (r.defenderSleeps && target.atk > 0) {
        state.log.add(`💤 ${target.name_ru} [${enemy.name}] спит — без ответа`);
      }
      await anim.afterAttack(unit, target, r.atkDmg, r.defDmg);

      // Триггеры урона: defender получил урон от unit, attacker — от ответки.
      const defDmgTaken = dmgTaken(r.fwd);
      if (defDmgTaken > 0) {
        onUnitDamaged(state, { ownerIdx: 1 - ownerIdx, card: target, dmg: defDmgTaken });
      }
      const atkDmgTaken = dmgTaken(r.bwd);
      if (atkDmgTaken > 0) {
        onUnitDamaged(state, { ownerIdx, card: unit, dmg: atkDmgTaken });
      }

      if (target.hp <= 0) {
        await anim.death(target);
        enemy.removeFromField(target);
        state.log.add(`✗ ${target.name_ru} [${enemy.name}] погибает`);
        anim.rerender();
        onUnitDied(state, {
          ownerIdx: 1 - ownerIdx, card: target,
          killer: unit, killerIdx: ownerIdx, killerWasCounter: false
        });
      }
      if (unit.hp <= 0) {
        await anim.death(unit);
        owner.removeFromField(unit);
        state.log.add(`✗ ${unit.name_ru} [${owner.name}] погибает`);
        anim.rerender();
        onUnitDied(state, {
          ownerIdx, card: unit,
          killer: target, killerIdx: 1 - ownerIdx, killerWasCounter: true
        });
      }
    }

    await anim.pause();
  }

  // В.2.a: Лив Лайф — раунд 2 хил-Аур (ПОСЛЕ авто-атак, диздок 6.4).
  // Без зарегистрированных Аур — no-op. Если Героев уже нет в живых,
  // ранний return мог сработать выше; если дошли сюда — оба ещё живы.
  runLivLifeHealAuras(state, 2);
}

// === Хвост лога с состоянием цели ДО→ПОСЛЕ удара ===
function tailHpArmor(before, after) {
  return ` (HP ${before.hp}→${after.hp}, броня ${before.armor}→${after.armor})`;
}

function formatAttackLog(attacker, attackerSide, target, targetSide, result, isHeroTarget) {
  const targetName = isHeroTarget ? shortHeroName(target) : target.name_ru;
  const fwd = result.fwd;
  const tag = (result.atkPierce && fwd.ignoredArmor > 0)
    ? ` (пробитие, броня ${fwd.ignoredArmor} игнор.)`
    : '';
  const before = result.defenderBefore || result.targetBefore;
  const after  = result.defenderAfter  || result.targetAfter;
  const tail = (before && after) ? tailHpArmor(before, after) : '';
  return `⚔ ${attacker.name_ru} [${attackerSide.name}] → ${targetName} [${targetSide.name}]: ${result.atkDmg} урона${tag}${tail}`;
}

function formatCounterLog(defender, defenderSide, attacker, attackerSide, result) {
  const bwd = result.bwd;
  const tag = (result.defPierce && bwd.ignoredArmor > 0)
    ? ` (пробитие, броня ${bwd.ignoredArmor} игнор.)`
    : '';
  const tail = (result.attackerBefore && result.attackerAfter)
    ? tailHpArmor(result.attackerBefore, result.attackerAfter)
    : '';
  return `⚔ ${defender.name_ru} [${defenderSide.name}] → ${attacker.name_ru} [${attackerSide.name}]: ${result.defDmg} урона (ответный)${tag}${tail}`;
}

// === Фазы 4 / 5: атака Героем ===
export function performHeroAttack(state, attackerIdx, target) {
  const attacker = state.players[attackerIdx];
  const enemy = state.players[1 - attackerIdx];
  const hero = attacker.hero;

  // Двойник Никоси: бонус-атака Героя. Если Герой уже атаковал в этом ходу,
  // но есть непогашенный счётчик heroBonusAttackPending (его начисляет
  // on-attack триггер Двойника), разрешаем ДОПОЛНИТЕЛЬНУЮ атаку и списываем
  // счётчик. Работает для ЛЮБОГО Героя (не привязано к Никоси).
  const usingBonus = hero.heroAttackedThisTurn && (attacker.heroBonusAttackPending || 0) > 0;
  const markHeroAttacked = () => {
    if (usingBonus) attacker.heroBonusAttackPending = Math.max(0, (attacker.heroBonusAttackPending || 0) - 1);
    else hero.heroAttackedThisTurn = true;
  };

  if (hero.heroAttackedThisTurn && (attacker.heroBonusAttackPending || 0) <= 0) {
    state.log.add(`${shortHeroName(hero)} [${attacker.name}] уже атаковал в этом ходу`);
    return false;
  }
  // В.1.g-fix2: Стратег Ли Пул — удар Героем взаимоисключаем с перемещением.
  if (attacker.heroMovedUnitThisTurn) {
    state.log.add(`${shortHeroName(hero)} [${attacker.name}] уже использовал перемещение в этой Ф4`);
    return false;
  }
  if (hero.hp <= 0) return false;
  if (!target || !target.isAlive || !target.isAlive()) {
    state.log.add(`Цель недоступна`);
    return false;
  }

  if (target.type === 'hero') {
    if (!canAttackHero(enemy)) {
      state.log.add(`Авангард: пока есть юниты, Героя бить нельзя`);
      return false;
    }
    // А.4.4.3: Секретарь перенаправляет урон по Герою.
    const r = redirectToSekretar(enemy, target);
    if (r.redirectedToSekretar) {
      target = r.target;
      state.log.add(`↪ Перенаправлено на Секретаря [${enemy.name}]`);
    }
  } else {
    // В.2.f-2-fix1: Скрытность побеждает Провокацию. Стелс-юнит «невидим»
    // даже если у него taunt — ни на нём не работает Провокация
    // (для атак Героя), ни его можно выбрать как taunt-цель.
    if (hasKeyword(target, KW.STEALTH)) {
      state.log.add(`${target.name_ru} невидим — атака недоступна`);
      return false;
    }
    // Taunt без стелса — обычная Провокация: цель должна быть taunt-юнитом
    // (если есть не-стелс taunt'ы на поле).
    const enemyTaunts = enemy.unitsOnField()
      .filter(u => hasKeyword(u, KW.TAUNT) && !hasKeyword(u, KW.STEALTH));
    if (enemyTaunts.length > 0 && !hasKeyword(target, KW.TAUNT)) {
      state.log.add(`Провокация: нужно атаковать taunt-юнита`);
      return false;
    }
    // В.2.e-2: untargetable.byEnemyHero — Подопытный Альфа недоступен для
    // атаки вражеского Героя. Молчание цели снимает флаг.
    if (target.untargetable?.byEnemyHero === true && !target.current?.silenced) {
      state.log.add(`${target.name_ru}: атака Героя недоступна (untargetable)`);
      return false;
    }
  }

  if (target.type === 'hero') {
    if (enemy.heroImmuneUntilTurn != null && state.currentTurn <= enemy.heroImmuneUntilTurn) {
      state.log.add(`🛡 ${shortHeroName(target)} [${enemy.name}]: Иммунитет — атака блокирована`);
      markHeroAttacked();
      onHeroAttack(state, { attackerIdx });
      return true;
    }
    const beforeArmor = target.armor;
    const r = heroVsHero(hero, target, { state });
    let parachuteFired = false;
    if (target.hp <= 0 && enemy.parachuteActive) {
      target.hp = 1;
      target.armor = beforeArmor;
      enemy.parachuteActive = false;
      enemy.heroImmuneUntilTurn = state.currentTurn;
      parachuteFired = true;
    }
    state.log.add(formatHeroAttackLog(hero, attacker, target, enemy, r, /*isHeroTarget=*/ true));
    if (parachuteFired) {
      state.log.add(`🪂 Золотой парашют [${enemy.name}]: смертельный удар отменён, ${shortHeroName(target)} HP=1, Иммунитет до конца хода`);
    }
    markHeroAttacked();
    onHeroAttack(state, { attackerIdx });
    // Б.2.c: триггер урона по Герою-защитнику.
    if (!parachuteFired) {
      const heroTaken = dmgTaken(r.fwd);
      if (heroTaken > 0) onHeroDamaged(state, 1 - attackerIdx, heroTaken);
    }
    if (target.hp <= 0) {
      state.log.add(`💀 ${shortHeroName(target)} [${enemy.name}] пал`);
    }
    // Этап 21: контратака защищающегося Героя по атакующему.
    const attackerTaken = dmgTaken(r.bwd);
    if (attackerTaken > 0) onHeroDamaged(state, attackerIdx, attackerTaken);
    if (hero.hp <= 0) {
      state.log.add(`💀 ${shortHeroName(hero)} [${attacker.name}] пал от контратаки`);
    }
  } else {
    // В.1.c-1: Расчёт тяжёлого пулемёта — стреляет ДО разрешения, если цель —
    // союзный для Расчёта juniт с Провокацией. Атакующий — Герой.
    const raschetShots = raschetOnAllyAttacked(state, attackerIdx, hero, 1 - attackerIdx, target);
    for (const shot of raschetShots) {
      const taken = (shot.dmg.armorAbsorbed || 0) + (shot.dmg.hpLost || 0);
      if (taken > 0) {
        // Герой получил урон — стандартный путь триггера.
        onHeroDamaged(state, attackerIdx, taken);
      }
    }
    if (hero.hp <= 0) {
      // Атакующий Герой пал от Расчёта — стандартный путь смерти Героя
      // обработается в checkMatchOver.
      markHeroAttacked();
      onHeroAttack(state, { attackerIdx });
      return true;
    }
    const r = heroVsUnit(hero, target, { state, currentTurn: state.currentTurn });
    state.log.add(formatHeroAttackLog(hero, attacker, target, enemy, r, /*isHeroTarget=*/ false));
    markHeroAttacked();
    onHeroAttack(state, { attackerIdx });
    const dmg = dmgTaken(r.fwd);
    if (dmg > 0) {
      onUnitDamaged(state, { ownerIdx: 1 - attackerIdx, card: target, dmg });
    }
    if (target.hp <= 0) {
      enemy.removeFromField(target);
      state.log.add(`✗ ${target.name_ru} [${enemy.name}] погибает`);
      onUnitDied(state, {
        ownerIdx: 1 - attackerIdx, card: target,
        killer: hero, killerIdx: attackerIdx, killerWasCounter: false
      });
    }
    // Этап 21: контратака защищающегося юнита по Герою.
    const heroTaken = dmgTaken(r.bwd);
    if (heroTaken > 0) onHeroDamaged(state, attackerIdx, heroTaken);
    if (hero.hp <= 0) {
      state.log.add(`💀 ${shortHeroName(hero)} [${attacker.name}] пал от контратаки`);
    }
  }
  return true;
}

// БОЙ.1.9: async-обёртка над performHeroAttack с анимациями.
// Pattern: snapshot ДО → anim.beforeAttack → original performHeroAttack
// (мутации) → diff → anim.afterAttack → if death anim.heroDeath/death.
// Дубликата логики НЕТ — все правила (Авангард, Провокация, Секретарь,
// Парашют, контратака, untargetable) остаются в performHeroAttack.
// Возвращает Promise<boolean> (true = удар прошёл, false = недоступна цель).
//
// Использование: BattleScreen.onPlayerHeroAttack, advanceTurn (бот),
// server/colyseus/match-room.handleHeroAttack.
export async function playHeroAttackWithAnim(state, attackerIdx, target, anim = NULL_ANIM) {
  const attackerPlayer = state.players[attackerIdx];
  const enemy = state.players[1 - attackerIdx];
  const hero = attackerPlayer.hero;

  // Лёгкие pre-checks — не мутируют. Полная валидация всё равно в performHeroAttack.
  if (!target || !target.isAlive || !target.isAlive()) {
    state.log.add(`Цель недоступна`);
    return false;
  }
  if (hero.hp <= 0) return false;

  // Preview redirect (Sekretar) — без мутаций, чтобы знать effectiveTarget для anim.
  let effectiveTarget = target;
  if (target.type === 'hero') {
    const r = redirectToSekretar(enemy, target);
    if (r.redirectedToSekretar) effectiveTarget = r.target;
  }

  // Snapshot stats ДО мутаций — для diff'а damages.
  const eTargetHpBefore = effectiveTarget.hp;
  const eTargetArmorBefore = effectiveTarget.armor;
  const heroHpBefore = hero.hp;
  const heroArmorBefore = hero.armor;

  // АНИМ.6: pre-check immune для атак Hero→Hero. performHeroAttack
  // ничего не зовёт у anim, но всё равно returns true — фаза переходит.
  // Чтобы юзер увидел блок — играем щит ДО attack.
  if (effectiveTarget.type === 'hero'
      && enemy.heroImmuneUntilTurn != null
      && state.currentTurn <= enemy.heroImmuneUntilTurn
      && typeof anim.heroImmune === 'function') {
    await anim.heroImmune(effectiveTarget);
    // Дальше performHeroAttack отработает (он сам делает return true для
    // Immune без damage), мы не зовём beforeAttack.
    const ok = performHeroAttack(state, attackerIdx, target);
    return !!ok;
  }

  await anim.beforeAttack(hero, effectiveTarget);

  // Original sync performHeroAttack — внутри валидирует Авангард/Провокацию,
  // делает redirect-Sekretar заново, мутирует hp/armor, пишет лог.
  const ok = performHeroAttack(state, attackerIdx, target);
  if (!ok) {
    // anim.beforeAttack уже выставил currentEvent — снимем чтобы не залипало.
    if (anim && 'currentEvent' in anim) {
      anim.currentEvent = null;
      if (typeof anim.notify === 'function') anim.notify();
    }
    // ПвП.8-fix-stuck-ghost: server-side RecordingAnim уже запушил
    // attack_start event. Без последующего attack_impact клиент получит
    // orphan-batch → ghost зависает в lunge. Откатываем последний event.
    if (typeof anim.rewindAttack === 'function') anim.rewindAttack();
    return false;
  }

  // Damages — через diff (стат-мутации применились внутри performHeroAttack).
  const atkDmg = (eTargetHpBefore - effectiveTarget.hp) + (eTargetArmorBefore - effectiveTarget.armor);
  const defDmg = (heroHpBefore - hero.hp) + (heroArmorBefore - hero.armor);

  anim.rerender();
  await anim.afterAttack(hero, effectiveTarget, Math.max(0, atkDmg), Math.max(0, defDmg));

  // АНИМ.8: если target — Hero, и его hp оказался <= 0 во время боя, но
  // performHeroAttack сделал hp=1 через Парашют — парашют сработал.
  // Snapshot was eTargetHpBefore; current effectiveTarget.hp = 1 ⇒
  // damage был >= eTargetHpBefore (смертельный) И сейчас hp ровно 1.
  if (effectiveTarget.type === 'hero'
      && effectiveTarget.hp === 1
      && eTargetHpBefore > 1
      && atkDmg >= eTargetHpBefore
      && typeof anim.parachute === 'function') {
    await anim.parachute(effectiveTarget);
  }

  // Смерть цели — heroDeath для Героя-цели или death для юнит-цели.
  // ВНИМАНИЕ: для unit-target performHeroAttack уже удалил из поля (removeFromField),
  // но JS-объект юнита остался + DOM-элемент с data-instance-id всё ещё в документе
  // до следующего React-ререндера. anim.death/heroDeath успевают рассчитать DOMRect.
  if (effectiveTarget.type === 'hero' && effectiveTarget.hp <= 0) {
    await anim.heroDeath(effectiveTarget);
  } else if (effectiveTarget.type !== 'hero' && effectiveTarget.hp <= 0) {
    await anim.death(effectiveTarget);
  }
  // Контратака могла убить нашего Героя.
  if (hero.hp <= 0) {
    await anim.heroDeath(hero);
  }

  anim.rerender();
  return true;
}

function formatHeroAttackLog(hero, attackerSide, target, targetSide, result, isHeroTarget) {
  const targetName = isHeroTarget ? shortHeroName(target) : target.name_ru;
  const fwd = result.fwd;
  const tag = (result.atkPierce && fwd.ignoredArmor > 0)
    ? ` (пробитие, броня ${fwd.ignoredArmor} игнор.)`
    : '';
  const tail = (result.targetBefore && result.targetAfter)
    ? tailHpArmor(result.targetBefore, result.targetAfter)
    : '';
  let line = `⚔ ${shortHeroName(hero)} [${attackerSide.name}] → ${targetName} [${targetSide.name}]: ${result.atkDmg} урона${tag}${tail}`;
  // Этап 21: контратака защищающегося. Показываем только если был встречный урон.
  if (result.bwd && (result.defDmg || 0) > 0 && !result.defenderSleeps && !result.counterSuppressed) {
    const bwd = result.bwd;
    const bTag = (result.defPierce && bwd.ignoredArmor > 0)
      ? ` (пробитие, броня ${bwd.ignoredArmor} игнор.)`
      : '';
    const bTail = (result.attackerBefore && result.attackerAfter)
      ? tailHpArmor(result.attackerBefore, result.attackerAfter)
      : '';
    line += ` | контратака ${result.defDmg}${bTag}${bTail}`;
  }
  return line;
}

// === Активация Скрытности (диздок 6.7) ===
export async function activateStealthUnit(state, attackerIdx, attacker, requestedTarget, anim = NULL_ANIM) {
  const owner = state.players[attackerIdx];
  const enemy = state.players[1 - attackerIdx];

  if (!attacker || !attacker.isAlive() || !hasKeyword(attacker, KW.STEALTH)) {
    return { ok: false, reason: 'no_stealth' };
  }
  if (!attacker.revealed || attacker.isSleeping(state.currentTurn)) {
    return { ok: false, reason: 'not_active' };
  }
  if (!requestedTarget || !requestedTarget.isAlive || !requestedTarget.isAlive()) {
    return { ok: false, reason: 'invalid_target' };
  }

  const justPlaced = attacker.placedTurn === state.currentTurn;
  let target = requestedTarget;
  let redirectedToTaunt = false;

  if (target.type === 'hero') {
    if (justPlaced) {
      state.log.add(`${attacker.name_ru} [${owner.name}]: первый ход — Героя атаковать нельзя`);
      return { ok: false, reason: 'just_placed_no_hero' };
    }
    // А.4.4.2-fix1 (баг 2): Авангард для стелс-атакующего.
    const visibleEnemy = enemy.unitsOnField().filter(u => !hasKeyword(u, KW.STEALTH));
    if (visibleEnemy.length > 0) {
      state.log.add(`${attacker.name_ru} [${owner.name}]: Авангард — Героя бить нельзя, есть видимые юниты противника`);
      return { ok: false, reason: 'avangard_blocks' };
    }
  } else {
    // В.2.f-2-fix1: стелс-taunt не считается taunt-целью (стелс невидим).
    const enemyTaunts = enemy.unitsOnField()
      .filter(u => hasKeyword(u, KW.TAUNT) && !hasKeyword(u, KW.STEALTH));
    if (enemyTaunts.length > 0 && !hasKeyword(target, KW.TAUNT)) {
      target = pickLeftmost(enemyTaunts);
      redirectedToTaunt = true;
    }
  }

  // Стелс снимается СРАЗУ — до разрешения удара (диздок 6.7).
  removeKeyword(attacker, KW.STEALTH);
  // Б.1.b: помечаем «стелс был использован» — блокирует повторную выдачу 'stealth' от Модов.
  attacker.stealthUsed = true;

  // А.4.4.3: Секретарь — перенаправление урона по Герою.
  let redirectedToSekretar = false;
  if (target.type === 'hero') {
    const r = redirectToSekretar(enemy, target);
    if (r.redirectedToSekretar) {
      target = r.target;
      redirectedToSekretar = true;
    }
  }

  const targetName = target.type === 'hero' ? shortHeroName(target) : target.name_ru;
  state.log.add(`👁 ${attacker.name_ru} [${owner.name}] активирован → ${targetName} [${enemy.name}]`);
  if (redirectedToTaunt) {
    state.log.add(`↪ Перенаправлено на ${target.name_ru} (Провокация)`);
  }
  if (redirectedToSekretar) {
    state.log.add(`↪ Перенаправлено на Секретаря [${enemy.name}]`);
  }

  // АНИМ.6: «выход из тени» — белая вспышка ПЕРЕД атакой стелс-юнита.
  if (typeof anim.stealthActivate === 'function') await anim.stealthActivate(attacker);

  await anim.beforeAttack(attacker, target);

  if (target.type === 'hero') {
    if (enemy.heroImmuneUntilTurn != null && state.currentTurn <= enemy.heroImmuneUntilTurn) {
      state.log.add(`🛡 ${shortHeroName(target)} [${enemy.name}]: Иммунитет — стелс-атака блокирована`);
      // ПвП.8-fix-stuck-ghost: beforeAttack уже запушил attack_start,
      // afterAttack не зовётся → orphan → ghost виснет.
      if (typeof anim.rewindAttack === 'function') anim.rewindAttack();
      // АНИМ.6: золотой щит на Hero.
      if (typeof anim.heroImmune === 'function') await anim.heroImmune(target);
      await anim.pause();
      return { ok: true, target, redirectedToTaunt, immune: true };
    }
    const beforeArmor = target.armor;
    const r = unitVsHero(attacker, target, { state });
    let parachuteFired = false;
    if (target.hp <= 0 && enemy.parachuteActive) {
      target.hp = 1;
      target.armor = beforeArmor;
      enemy.parachuteActive = false;
      enemy.heroImmuneUntilTurn = state.currentTurn;
      parachuteFired = true;
    }
    anim.rerender();
    state.log.add(formatAttackLog(attacker, owner, target, enemy, r, /*isHeroTarget=*/ true));
    if (parachuteFired) {
      state.log.add(`🪂 Золотой парашют [${enemy.name}]: смертельный удар отменён, ${shortHeroName(target)} HP=1, Иммунитет до конца хода`);
    }
    // Б.2.c: триггер урона по Герою (для стелс-атаки).
    if (!parachuteFired) {
      const heroTaken = dmgTaken(r.fwd);
      if (heroTaken > 0) onHeroDamaged(state, 1 - attackerIdx, heroTaken);
    }
    await anim.afterAttack(attacker, target, r.atkDmg);
    // АНИМ.8: парашют играем ПОСЛЕ afterAttack — чтобы юзер сначала
    // увидел удар, потом золотой парашют спасает.
    if (parachuteFired && typeof anim.parachute === 'function') {
      await anim.parachute(target);
    }
    if (target.hp <= 0) {
      await anim.heroDeath(target);
      state.log.add(`💀 ${shortHeroName(target)} [${enemy.name}] пал`);
    }
  } else {
    // В.1.c-1: Расчёт тяжёлого пулемёта — стреляет ДО разрешения боя.
    const raschetShots = raschetOnAllyAttacked(state, attackerIdx, attacker, 1 - attackerIdx, target);
    for (const shot of raschetShots) {
      // АНИМ.9: пунктирный красный луч от Расчёта к стелс-attacker'у.
      if (typeof anim.raschet === 'function' && shot.raschet) {
        await anim.raschet(shot.raschet, attacker);
      }
      const taken = (shot.dmg.armorAbsorbed || 0) + (shot.dmg.hpLost || 0);
      if (taken > 0) {
        onUnitDamaged(state, { ownerIdx: attackerIdx, card: attacker, dmg: taken });
      }
      if (attacker.hp <= 0) {
        owner.removeFromField(attacker);
        state.log.add(`✗ ${attacker.name_ru} [${owner.name}] погибает (от Расчёта)`);
        onUnitDied(state, {
          ownerIdx: attackerIdx, card: attacker,
          killer: shot.raschet, killerIdx: 1 - attackerIdx, killerWasCounter: false
        });
        break;
      }
    }
    if (!attacker.isAlive()) {
      // ПвП.8-fix-stuck-ghost: beforeAttack уже запушил attack_start,
      // attacker умер от Расчёта до afterAttack → orphan.
      if (typeof anim.rewindAttack === 'function') anim.rewindAttack();
      await anim.pause();
      return { ok: true, target, redirectedToTaunt };
    }
    const r = unitVsUnit(attacker, target, state.currentTurn, { state });
    anim.rerender();
    state.log.add(formatAttackLog(attacker, owner, target, enemy, r, /*isHeroTarget=*/ false));
    if (r.defDmg > 0) {
      state.log.add(formatCounterLog(target, enemy, attacker, owner, r));
    } else if (r.defenderSleeps && target.atk > 0) {
      state.log.add(`💤 ${target.name_ru} [${enemy.name}] спит — без ответа`);
    }
    await anim.afterAttack(attacker, target, r.atkDmg, r.defDmg);

    const defDmgTaken = dmgTaken(r.fwd);
    if (defDmgTaken > 0) {
      onUnitDamaged(state, { ownerIdx: 1 - attackerIdx, card: target, dmg: defDmgTaken });
    }
    const atkDmgTaken = dmgTaken(r.bwd);
    if (atkDmgTaken > 0) {
      onUnitDamaged(state, { ownerIdx: attackerIdx, card: attacker, dmg: atkDmgTaken });
    }

    if (target.hp <= 0) {
      await anim.death(target);
      enemy.removeFromField(target);
      state.log.add(`✗ ${target.name_ru} [${enemy.name}] погибает`);
      anim.rerender();
      onUnitDied(state, {
        ownerIdx: 1 - attackerIdx, card: target,
        killer: attacker, killerIdx: attackerIdx, killerWasCounter: false
      });
    }
    if (attacker.hp <= 0) {
      await anim.death(attacker);
      owner.removeFromField(attacker);
      state.log.add(`✗ ${attacker.name_ru} [${owner.name}] погибает`);
      anim.rerender();
      onUnitDied(state, {
        ownerIdx: attackerIdx, card: attacker,
        killer: target, killerIdx: 1 - attackerIdx, killerWasCounter: true
      });
    }
  }
  await anim.pause();

  return { ok: true, target, redirectedToTaunt };
}

function pickLeftmost(units) {
  const sorted = [...units].sort((a, b) => {
    if (a.line !== b.line) return a.line - b.line;
    return a.slot - b.slot;
  });
  return sorted[0];
}

// === Хелперы для UI/бота ===
export function getActivatableStealth(state, playerIdx) {
  const p = state.players[playerIdx];
  const isBot = playerIdx === 1;
  const out = [];
  for (const u of p.unitsOnField()) {
    if (!hasKeyword(u, KW.STEALTH)) continue;
    if (!u.revealed || u.isSleeping(state.currentTurn)) continue;
    if (isBot && u.placedTurn === state.currentTurn) continue;
    out.push(u);
  }
  return out;
}

export function getStealthTargets(state, attackerIdx, attacker) {
  const enemy = state.players[1 - attackerIdx];
  const justPlaced = attacker.placedTurn === state.currentTurn;
  // В.2.f-2-fix1: стелс-taunt не считается taunt-целью (стелс невидим).
  const enemyTaunts = enemy.unitsOnField()
    .filter(u => hasKeyword(u, KW.TAUNT) && !hasKeyword(u, KW.STEALTH));
  const visibleEnemy = enemy.unitsOnField()
    .filter(u => !hasKeyword(u, KW.STEALTH))
    .filter(u => u.id !== SPY_ID);                       // А.4.4.4: spy не считается для Авангарда
  let units;
  if (enemyTaunts.length > 0) {
    units = enemyTaunts;
  } else {
    units = visibleEnemy;
  }
  // А.4.4.2-fix1 (баг 2): стелс-атакующий тоже подчиняется Авангарду.
  // Герой доступен только если у врага нет ни одного НЕ-стелс юнита (включая taunt-ов).
  // Если все живые враги — стелс, тупик предотвращаем: Герой уязвим и для моего стелса.
  const heroAllowed = !justPlaced
    && enemyTaunts.length === 0
    && visibleEnemy.length === 0
    && enemy.hero.hp > 0;
  return { units, heroAllowed };
}

// === Конец хода ===
// runEndOfTurnTriggers вызывается ДО endOfTurnCleanup из match.js.
// А.4.4.4-fix1: Спящие больше НЕ возвращаются автоматически в EOT.
// Возврат происходит только в Ф3 при срабатывании активированного спящего.
export function runEndOfTurnTriggers(state) {
  onEndOfTurn(state);
}

export function endOfTurnCleanup(state) {
  // БАГ.25 (safety net): иногда юнит с hp<=0 остаётся в field[line][slot]
  // потому что какой-то путь нанесения урона не вызвал removeFromField
  // (триггеры, EOT-эффекты, Действия). Гарантируем что в конце хода
  // мёртвые юниты убраны с поля и добавлены в discard.
  for (const p of state.players) {
    for (let line = 0; line < 2; line++) {
      for (let slot = 0; slot < 4; slot++) {
        const u = p.field?.[line]?.[slot];
        if (u && (u.hp <= 0 || u.alive === false)) {
          state.log?.add?.(`⚠ EOT-cleanup: ${u.name_ru || u.id} с hp=${u.hp} убран с поля`);
          p.removeFromField(u);
        }
      }
    }
  }
  for (const p of state.players) {
    p.hero.heroAttackedThisTurn = false;
    // Двойник Никоси: сбрасываем непогашенный счётчик бонус-атак Героя
    // (зарабатывается в Ф3, тратится в Ф4/Ф5 того же хода).
    p.heroBonusAttackPending = 0;
    // В.1.g-fix2: сбросить флаг перемещения Стратега за этот ход.
    p.heroMovedUnitThisTurn = false;
    // Снимаем истёкшие полосы временной брони (Активный щит).
    cleanupTempArmorBands(p.hero, state.currentTurn);
    p.hero.restoreArmor();
    cleanupTransientKeywords(p.hero, state.currentTurn);
    for (const u of p.unitsOnField()) {
      cleanupTempArmorBands(u, state.currentTurn);
      u.restoreArmor();
      // В.1.f-1: истёкшие transient-keyword'ы (Перегруппировка → frenzy на ход).
      cleanupTransientKeywords(u, state.currentTurn);
    }
    // Сброс блокировки Влияния (если истекла).
    if (p.blockInfluenceUntilTurn != null && state.currentTurn >= p.blockInfluenceUntilTurn) {
      p.blockInfluenceUntilTurn = null;
    }
    // Иммунитет Героя действует до конца хода — снимается тут.
    if (p.heroImmuneUntilTurn != null && state.currentTurn >= p.heroImmuneUntilTurn) {
      p.heroImmuneUntilTurn = null;
    }
  }
}

function cleanupTempArmorBands(card, currentTurn) {
  if (!card.tempArmorBands || card.tempArmorBands.length === 0) return;
  card.tempArmorBands = card.tempArmorBands.filter(b => currentTurn < b.expireAfterTurn);
}

// АНИМ.7: snapshot всех юнитов на поле + Героев для diff'а после
// resolveAction. Возвращает Map<instanceId, {atk, armor, hp, ownerIdx, alive}>.
function _snapshotUnits(state) {
  const map = new Map();
  for (let i = 0; i < 2; i++) {
    const p = state.players[i];
    if (!p) continue;
    for (const u of p.unitsOnField()) {
      // АНИМ.15: snapshot keyword'ов — для детекции наложения/снятия.
      // Учитываем current.keywords (с учётом Молчания и баффов), не base.
      const effKw = u.current?.keywords || u.keywords || [];
      const isSilenced = !!(u.current?.silenced || (u.silenceSources && u.silenceSources.size > 0));
      map.set(u.instanceId, {
        atk: u.current?.atk ?? u.atk ?? 0,
        armor: u.current?.armor ?? u.armor ?? 0,
        hp: u.current?.hp ?? u.hp ?? 0,
        // АНИМ.11: запоминаем line/slot для детекции MOVE.
        line: u.line,
        slot: u.slot,
        // АНИМ.14: счётчики Mod / Infected для stack-up детекции.
        modCount: Array.isArray(u.modifications) ? u.modifications.length : 0,
        infectedStacks: u.infectedStacks || 0,
        // АНИМ.15: keyword'ы для детекции attach/remove.
        keywords: new Set(effKw),
        silenced: isSilenced,
        ownerIdx: i,
        ref: u,
        type: 'unit',
      });
    }
    // Hero (instanceId либо 0 либо undefined в engine — используем -100-i как уникальный).
    if (p.hero) {
      const heroKey = `hero:${i}`;
      map.set(heroKey, {
        atk: p.hero.current?.atk ?? p.hero.atk ?? 0,
        armor: p.hero.current?.armor ?? p.hero.armor ?? 0,
        hp: p.hero.current?.hp ?? p.hero.hp ?? 0,
        ownerIdx: i,
        ref: p.hero,
        type: 'hero',
      });
    }
  }
  return map;
}

// АНИМ.7: после resolveAction проигрываем визуалы: heal/buff/summon/damage.
async function _playActionDiffAnims(before, after, state, anim) {
  // Новые юниты — SUMMON.
  for (const [id, a] of after) {
    if (a.type !== 'unit') continue;
    if (!before.has(id)) {
      if (typeof anim.summon === 'function') await anim.summon(a.ref);
    }
  }
  // Изменения stat'ов — heal/buff/damage. Также death от Действия:
  // юнит был в before, нет в after (или есть но removed from field) → death.
  for (const [id, b] of before) {
    const a = after.get(id);
    if (!a) {
      // АНИМ.7-fix-death-action: юнит умер от Действия (например Огневой
      // вызов добил). Не auto-attack — Phase3 death-flow не сработает.
      if (b.type === 'unit' && typeof anim.death === 'function') {
        await anim.death(b.ref);
      }
      continue;
    }
    // АНИМ.11: перемещение юнита — приоритет над heal/buff/damage. Если
    // юнит сменил line/slot — играем move ghost-летит из старой ячейки.
    // line/slot есть только у type 'unit' (Hero не перемещается).
    if (a.type === 'unit' && b.type === 'unit'
        && (a.line !== b.line || a.slot !== b.slot)) {
      if (typeof anim.move === 'function') {
        await anim.move(a.ref, b.line, b.slot, a.line, a.slot, a.ownerIdx);
      }
      continue;
    }
    // АНИМ.14: stack-up — Mod добавлен / стак Infected++. Играется
    // ДО stat-diff (Mod может дать +atk одновременно — stack-up flashes
    // first, then buff overlay).
    if (a.type === 'unit' && b.type === 'unit') {
      if (a.modCount > b.modCount && typeof anim.stackUp === 'function') {
        await anim.stackUp(a.ref, 'mod', a.modCount);
      }
      if (a.infectedStacks > b.infectedStacks && typeof anim.stackUp === 'function') {
        await anim.stackUp(a.ref, 'infected', a.infectedStacks);
      }
    }
    // АНИМ.15: keyword attach/remove flash. Кроме infected — он покрыт
    // stack-up'ом выше (флаш ☣ от stackUp). Сильно полезно для Молчания
    // (silence не keyword а флаг), Стелс/Провок/Беш/Прб/Иммунитет.
    if (a.type === 'unit' && b.type === 'unit'
        && typeof anim.keywordFlash === 'function') {
      // Silenced — отдельный флаг (b.silenced/a.silenced), не keyword.
      if (a.silenced && !b.silenced) {
        await anim.keywordFlash(a.ref, 'silence', 'add');
      } else if (!a.silenced && b.silenced) {
        await anim.keywordFlash(a.ref, 'silence', 'remove');
      }
      // Keyword'ы из массива — учитываем только «именованные» интересные.
      const FLASH_KW = ['stealth', 'taunt', 'frenzy', 'pierce',
                        'immune_first_hit', 'suppression'];
      for (const kw of FLASH_KW) {
        const had = b.keywords.has(kw);
        const has = a.keywords.has(kw);
        if (has && !had) await anim.keywordFlash(a.ref, kw, 'add');
        else if (!has && had) await anim.keywordFlash(a.ref, kw, 'remove');
      }
    }
    const dAtk = a.atk - b.atk;
    const dArmor = a.armor - b.armor;
    const dHp = a.hp - b.hp;
    if (dHp < 0) {
      // Damage без attacker'а (Огневой вызов и т.п.). dAtk/dArmor
      // дебаффы вместе с damage редко — отдельной анимации не нужно.
      if (typeof anim.damage === 'function') await anim.damage(a.ref, -dHp);
    } else if (dAtk > 0 || dArmor > 0 || (dHp > 0 && (dAtk !== 0 || dArmor !== 0))) {
      // Buff: есть прирост atk или armor (даже если hp тоже +).
      if (typeof anim.buff === 'function')
        await anim.buff(a.ref, Math.max(0, dAtk), Math.max(0, dArmor), Math.max(0, dHp));
    } else if (dHp > 0) {
      // Только hp ↑ — pure heal.
      if (typeof anim.heal === 'function') await anim.heal(a.ref, dHp);
    } else if (dAtk < 0 || dArmor < 0) {
      // АНИМ.13: debuff — атака/броня снижены без потери hp.
      // Истечение временных полос брони/атаки, дебаффы от Действий
      // (Глушилка, Огневая поддержка), Молчание снимает бонусы.
      if (typeof anim.buff === 'function') {
        await anim.buff(a.ref, dAtk, dArmor, dHp);  // отрицательные передаём как есть
      }
    }
  }
  // АНИМ.10-fix-hero-death-from-action: Hero не удаляется из state.players[i]
  // когда hp <= 0 — он остаётся в snapshot'е обеих. Поэтому отдельная проверка:
  // если Hero был жив в before и hp <= 0 в after — играем heroDeath.
  for (const [id, b] of before) {
    if (b.type !== 'hero') continue;
    const a = after.get(id);
    if (!a) continue;
    if (b.hp > 0 && a.hp <= 0 && typeof anim.heroDeath === 'function') {
      await anim.heroDeath(b.ref);
    }
  }
}

// АНИМ.10: универсальная обёртка — snapshot перед sync-fn, snapshot после,
// проигрываем diff (heal/buff/summon/damage/death/heroDeath). Используется
// для runUnitOrdersOnReveal / revealPendingMods / useHeroActive / phaseDraw —
// везде где sync-код мутирует state и мы хотим увидеть изменения визуально.
async function _withDiffAnim(state, anim, syncFn) {
  if (!anim || anim === NULL_ANIM) {
    syncFn(state);
    return;
  }
  const before = _snapshotUnits(state);
  syncFn(state);
  const after = _snapshotUnits(state);
  await _playActionDiffAnims(before, after, state, anim);
  if (typeof anim.rerender === 'function') anim.rerender();
}

// АНИМ.10: публичный wrapper над _withDiffAnim — для использования из
// colyseus match-room.ts (handleHeroActive и т.п.). Снимает before-snapshot,
// зовёт syncFn(state), снимает after-snapshot, проигрывает diff'ы
// heal/buff/summon/damage/death/heroDeath.
export async function runSyncWithDiff(state, anim, syncFn) {
  return _withDiffAnim(state, anim, syncFn);
}

// А.4.4.3: Резолв Действий, выбранных в Ф2. Порядок: первый игрок, потом второй.
async function resolvePendingActions(state, anim) {
  const order = [state.firstPlayerIdx, 1 - state.firstPlayerIdx];
  for (const idx of order) {
    const p = state.players[idx];
    const pa = p.pendingAction;
    if (!pa) continue;
    p.pendingAction = null;
    // Разыграть карту: найти её в руке (или создать резерв) и резолвить.
    const handIdx = p.hand.findIndex(c => c.instanceId === pa.cardInstanceId);
    if (handIdx === -1) continue;
    const card = p.hand.splice(handIdx, 1)[0];
    // Найти цель по targetInstanceId (юнит на поле обоих игроков).
    let target = null;
    if (pa.targetInstanceId != null) {
      for (const pp of state.players) {
        for (const u of pp.unitsOnField()) {
          if (u.instanceId === pa.targetInstanceId) { target = u; break; }
        }
        if (target) break;
      }
    }
    state.log.add(`🎴 ${p.name}: Действие "${card.name_ru}"`);
    // В.1.h-fix1: эпик/легенда Действие, разыгранное РАНЬШЕ положенного хода —
    // сгорает без эффекта.
    const minTurn = actionInitiationTurn(card.rarity);
    // SANDBOX без инициации (_sandboxNoInit) — Действие не сгорает раньше срока.
    if (!card._sandboxNoInit && state.currentTurn < minTurn) {
      state.log.add(
        `🔥 ${card.name_ru}: разыграна раньше ${minTurn}-го хода ` +
        `(${card.rarity === 'legendary' ? 'легендарная' : 'эпическая'}) — ` +
        `сгорела без эффекта`
      );
      p.discard.push(card);
      rememberLastAction(state, idx, card.id);
      anim.rerender();
      await anim.pause();
      continue;
    }
    // АНИМ.7: PLAY ACTION — превью карты в центре + волна к target'у.
    if (typeof anim.playAction === 'function') await anim.playAction(card, target);
    // АНИМ.7: snapshot state ДО resolveAction для diff'а эффектов.
    const beforeSnap = _snapshotUnits(state);
    // В.1.f-1: 4-й аргумент — opts (доп. поля из pendingAction).
    resolveAction(state, idx, card.id, target, pa.opts || {});
    // АНИМ.7: diff и проигрываем heal/buff/summon/damage.
    const afterSnap = _snapshotUnits(state);
    await _playActionDiffAnims(beforeSnap, afterSnap, state, anim);
    p.discard.push(card);
    rememberLastAction(state, idx, card.id);
    anim.rerender();
    await anim.pause();
    if (state.players[0].hero.hp <= 0 || state.players[1].hero.hp <= 0) return;
  }
}
